import { NavLink } from "react-router-dom";
import {
  Activity, RefreshCw, Shuffle, TrendingUp, ShieldCheck, Share2, Target,
  LayoutDashboard, Zap, Wallet, Trophy, LineChart, Upload, Settings, Cpu, X,
} from "lucide-react";
import { ENGINES } from "../../config/engines";
import { T } from "../../lib/testids";

const ICONS = {
  activity: Activity,
  "refresh-cw": RefreshCw,
  shuffle: Shuffle,
  "trending-up": TrendingUp,
  "shield-check": ShieldCheck,
  "share-2": Share2,
  target: Target,
};

const OPERATOR_LINKS = [
  { to: "/", label: "Hoy", icon: LayoutDashboard, testid: "nav-home" },
  { to: "/turn", label: "Trabajo de hoy", icon: Zap, testid: "nav-turn" },
  { to: "/wallet", label: "Mi cartera", icon: Wallet, testid: "nav-wallet" },
  { to: "/closures", label: "Cierres", icon: Trophy, testid: "nav-closures" },
];

const INTELLIGENCE_LINKS = [
  { to: "/executive", label: "Panorama ejecutivo", icon: LayoutDashboard, testid: "nav-executive" },
  { to: "/forecast", label: "Proyección de ingresos", icon: LineChart, testid: "nav-forecast" },
];

const SYSTEM_LINKS = [
  { to: "/ingestion", label: "Cargar cartera", icon: Upload, testid: "nav-ingestion" },
  { to: "/settings", label: "Configuración", icon: Settings, testid: "nav-settings" },
];

function Section({ title, items, onNavigate }) {
  return (
    <>
      <div className="text-[9px] text-zinc-600 uppercase tracking-[0.2em] px-3 mb-2 mt-5">
        {title}
      </div>
      <ul className="space-y-1 mb-2">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                onClick={onNavigate}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${
                    isActive
                      ? "bg-white/[0.06] text-amber-400"
                      : "text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.03]"
                  }`
                }
              >
                <Icon size={14} strokeWidth={1.75} />
                <span className="flex-1">{item.label}</span>
              </NavLink>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/**
 * Responsive (Sprint 12 móvil): en escritorio (lg+) es exactamente el mismo
 * sidebar estático de siempre — `lg:static lg:translate-x-0` neutraliza todo
 * lo de abajo. Por debajo de `lg`, se vuelve un panel fijo que entra/sale de
 * pantalla (`open`/`onClose`, controlado por AppShell) en vez de compartir
 * ancho con el contenido — mismos links, mismo orden, misma navegación.
 */
export default function Sidebar({ open = false, onClose }) {
  return (
    <aside
      data-testid={T.sidebar}
      className={`fixed inset-y-0 left-0 z-50 w-[260px] shrink-0 border-r border-white/[0.06] bg-[#0d1321] flex flex-col transition-transform duration-200 lg:static lg:translate-x-0 ${
        open ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      <div className="px-5 py-6 border-b border-white/[0.06]">
        <div className="flex items-center justify-between gap-2.5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-amber-500 text-black">
              <Cpu size={18} strokeWidth={2.5} />
            </div>
            <div className="leading-tight">
              <div className="font-display font-black text-[15px] tracking-tight">
                COMMERCIAL
                <span className="text-amber-500">.</span>
                OPERATOR
              </div>
              <div className="text-[9px] text-zinc-500 uppercase tracking-[0.2em] mt-0.5">
                Tu director comercial digital
              </div>
            </div>
          </div>
          <button
            type="button"
            data-testid={T.sidebarClose}
            onClick={onClose}
            aria-label="Cerrar menú"
            className="lg:hidden shrink-0 w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-zinc-200"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <nav className="flex-1 py-4 px-3 overflow-y-auto">
        <Section title="Mi trabajo" items={OPERATOR_LINKS} onNavigate={onClose} />

        <div className="text-[9px] text-zinc-600 uppercase tracking-[0.2em] px-3 mb-2 mt-5">
          Categorías de trabajo
        </div>
        <ul className="space-y-1 mb-2">
          {ENGINES.map((e) => {
            const Icon = ICONS[e.icon] || Activity;
            const isLive = e.status === "live";
            return (
              <li key={e.id}>
                <NavLink
                  to={e.path}
                  data-testid={T.sidebarEngineLink(e.id)}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2 rounded-xl text-sm transition-colors ${
                      isActive
                        ? isLive
                          ? "bg-white/[0.06] text-amber-400"
                          : "bg-white/[0.06] text-zinc-300"
                        : isLive
                          ? "text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.03]"
                          : "text-zinc-600 hover:text-zinc-400 hover:bg-white/[0.03]"
                    }`
                  }
                >
                  <Icon size={14} strokeWidth={1.75} />
                  <span className="flex-1">{e.humanLabel}</span>
                  {isLive ? (
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 tracking-[0.15em] font-mono">
                      Activo
                    </span>
                  ) : (
                    <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-white/[0.04] text-zinc-500 tracking-[0.1em] font-mono">
                      Próximamente
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>

        <Section title="Resultados" items={INTELLIGENCE_LINKS} onNavigate={onClose} />
        <Section title="Configuración" items={SYSTEM_LINKS} onNavigate={onClose} />
      </nav>

      <div className="p-4 border-t border-white/[0.06]">
        <div className="text-[10px] text-zinc-500 leading-snug">
          Trabaja sobre la información real de tu negocio, sin duplicar nada.
        </div>
      </div>
    </aside>
  );
}
